<div
  class="uploader-panel"
  [class.visible]="visible"
  [class.collapsed]="uploader.getState() != uploader.state.IDLE || uploader.showDragHint"
  data-test-qa="uploaderPanel"
>
  <div class="header" (click)="visible = !visible">
    @if (!uploader.showDragHint) {
      <div
        class="progress"
        [style.width.%]="Math.floor(uploader.getProgress() * 100)"
        data-test-qa="progressBar"
      ></div>
    }
    @if (!uploader.showDragHint) {
      <div class="content">
        <i class="fad fa-upload m-4"></i>
        @if (uploader.getState() == uploader.state.IDLE) {
          <span translate>Uploads</span>
        }
        @if (uploader.getState() == uploader.state.UPLOADING) {
          <span>
            <span translate>Upload in progress</span>
            <span class="upload-progress ms-1">– {{ getTotalProgress() }}%</span>
            @if (humanReadableUploadSpeed$ | async; as speed) {
              <span class="upload-progress ms-1">– {{ speed | bytes }}/s</span>
            }
          </span>
        }
        @if (uploader.getState() == uploader.state.PAUSED) {
          <span>
            <span translate>Upload paused</span>
            <span class="upload-progress ms-1">– {{ getTotalProgress() }}%</span>
          </span>
        }
        @if (uploader.getState() == uploader.state.DONE) {
          <span data-test-qa="uploadComplete">
            @if (!haveErrors()) {
              <span translate>Upload complete</span>
            }
            @if (haveErrors()) {
              <span translate>Upload failed</span>
            }
          </span>
        }
        <button
          class="btn float-right"
          (click)="clear(); $event.stopPropagation()"
          data-test-qa="clearButton"
        >
          <i class="far fa-times"></i>
          <span translate>Close</span>
        </button>
      </div>
    }
    @if (uploader.showDragHint) {
      <div class="content">
        <span class="m-auto" translate>Drop to upload</span>
      </div>
    }
  </div>
  <div class="content" [formGroup]="form">
    <div class="uploads-container">
      @if (uploaderFlowFiles.length == 0) {
        <empty-state
          [text]="'No files in the upload queue' | translate"
          [hint]="'Drag files here to upload' | translate"
          icon="copy"
        />
      }
      <div class="list-group list-group-light uploads-list">
        @for (file of uploaderFlowFiles; track file.fullPath) {
          <div class="list-group-item combi single" [class.active]="selectedFile == file">
            <a
              class="list-group-main no-wrap"
              (click)="selectFile(file)"
              [class.disabled]="judgeMetadataValidity(file) === 'valid' && !file.pending"
              [title]="file.name"
            >
              @if (judgeMetadataValidity(file) === "invalid") {
                <badge scheme="default">
                  <badge-prefix><i class="far fa-fw fa-ellipsis-h"></i></badge-prefix>
                  <span translate>INCOMPLETE METADATA</span>
                </badge>
              }
              @if (judgeMetadataValidity(file) === "valid") {
                @if (file.error) {
                  <badge scheme="danger">
                    <badge-prefix><i class="far fa-fw fa-exclamation-triangle"></i></badge-prefix>
                    <span translate>FAILED</span>
                  </badge>
                }
                @if (file.paused && !file.pending) {
                  <badge scheme="warning">
                    <badge-prefix><i class="far fa-fw fa-pause"></i></badge-prefix>
                    <span translate>PAUSED</span>
                    <span class="upload-progress ms-1">- {{ getFileProgress(file) }}%</span>
                  </badge>
                }
                @if (file.paused && file.pending) {
                  <badge scheme="info">
                    <badge-prefix><i class="far fa-fw fa-ellipsis-h"></i></badge-prefix>
                    <span translate>PENDING</span>
                  </badge>
                }
                @if (file.done) {
                  <badge scheme="success">
                    <badge-prefix><i class="far fa-fw fa-check"></i></badge-prefix>
                    <span translate>DONE</span>
                  </badge>
                }
                @if (file.isUploading() && !file.paused) {
                  <badge scheme="info">
                    <badge-prefix><i class="far fa-fw fa-cloud-upload"></i></badge-prefix>
                    <span translate>UPLOADING</span>
                    <span class="upload-progress ms-1">- {{ getFileProgress(file) }}%</span>
                  </badge>
                }
                @if (file.finishing) {
                  <badge scheme="info">
                    <badge-prefix><i class="far fa-fw fa-ellipsis-h"></i></badge-prefix>
                    <span translate>FINISHING</span>
                  </badge>
                }
              }
              <span class="ms-2">{{ file.relativePath }}</span>
            </a>
            @if (file.paused && file.pending && judgeMetadataValidity(file) == "valid") {
              <button class="btn btn-link reveal" (click)="startFile(file)">
                <i class="far fa-play"></i>
                <span translate>Start</span>
              </button>
            }
            @if (file.paused && !file.pending) {
              <button
                class="btn btn-link reveal"
                (click)="startFile(file)"
                [title]="'Resume' | translate"
              >
                <i class="far fa-play"><span class="visually-hidden" translate>Resume</span></i>
              </button>
            }
            @if (file.error) {
              <button
                class="btn btn-link reveal"
                (click)="retry(file)"
                [title]="'Retry' | translate"
                data-test-qa="retryButton"
              >
                <i class="far fa-rotate-right">
                  <span class="visually-hidden" translate>Retry</span>
                </i>
              </button>
            }
            @if (file.isUploading() && !file.paused) {
              <button
                class="btn btn-link reveal"
                (click)="pause(file)"
                [title]="'Pause' | translate"
              >
                <i class="far fa-pause"><span class="visually-hidden" translate>Pause</span></i>
              </button>
            }
            @if (!file.error && !file.done) {
              <button
                class="btn btn-link reveal"
                (click)="file.cancel(); updateSelectedTab()"
                [title]="'Cancel' | translate"
              >
                <i class="far fa-trash-alt">
                  <span class="visually-hidden" translate>Cancel</span>
                </i>
              </button>
            }
          </div>
        }
      </div>
    </div>
    @if (haveFields()) {
      <div class="metadata-container bg-dark">
        <ul class="nav-pills" ngbNav #nav="ngbNav">
          <li ngbNavItem="fallbacks">
            <a ngbNavLink>
              <i class="far fa-infinity text-info me-2"></i>
              <span translate>Shared metadata</span>
              @if (form.controls.fallbacks.invalid) {
                <i class="far fa-triangle-exclamation text-danger ms-2"></i>
              }
            </a>
            <ng-container *ngbNavContent formGroupName="fallbacks">
              <ng-container formGroupName="customFields">
                @for (field of fields; track field.name) {
                  <custom-field [name]="field.name">
                    <versatile-field
                      customFieldValue
                      [definition]="field.definition"
                      [formControlName]="field.name"
                      [ngbTooltip]="field.tooltip"
                      placement="bottom"
                      [error]="
                        form.controls.fallbacks.controls.customFields.controls[field.name]!.invalid
                      "
                      emphasizeAlmostNullValues
                      [getAutocompletions]="customFields.createAutocompletionGetter(field.name)"
                    />
                    @if (analyzeRequirednessConstraintForFallback(field.name) === "satisfied") {
                      <custom-field-suffix
                        class="square"
                        [ngbTooltip]="'This field is required.' | translate"
                      >
                        <i class="far fa-asterisk text-success"></i>
                      </custom-field-suffix>
                    }
                    @if (analyzeRequirednessConstraintForFallback(field.name) === "violated") {
                      <custom-field-suffix
                        class="square"
                        [ngbTooltip]="'This field is required.' | translate"
                      >
                        <i class="far fa-asterisk text-danger"></i>
                      </custom-field-suffix>
                    }
                    @if (
                      analyzeRequirednessConstraintForFallback(field.name) === "satisfied-manually"
                    ) {
                      <custom-field-suffix
                        class="square"
                        [ngbTooltip]="
                          'This field is required, and its value was supplied for each file individually.'
                            | translate
                        "
                      >
                        <i class="far fa-asterisk text-success"></i>
                      </custom-field-suffix>
                    }
                    @if (usedAsFallback(field.name) === "always") {
                      <custom-field-prefix
                        class="square"
                        [ngbTooltip]="'This shared value is used for all files.' | translate"
                      >
                        <i class="far fa-infinity text-info"></i>
                      </custom-field-prefix>
                    }
                    @if (usedAsFallback(field.name) === "sometimes") {
                      <custom-field-prefix
                        class="square"
                        [ngbTooltip]="
                          'This shared value is used for some files and overridden for some files.'
                            | translate
                        "
                      >
                        <i class="far fa-infinity text-info"></i>
                      </custom-field-prefix>
                    }
                    @if (usedAsFallback(field.name) === "never") {
                      <custom-field-prefix
                        class="square"
                        [ngbTooltip]="
                          'This shared value is overridden by each file individually, so it remains unused.'
                            | translate
                        "
                      >
                        <i class="far fa-infinity text-muted"></i>
                      </custom-field-prefix>
                    }
                    @if (
                      form.controls.fallbacks.controls.customFields.controls[field.name]!.invalid
                    ) {
                      <custom-field-suffix
                        class="square"
                        [ngbTooltip]="
                          'This value does not satisfy the validation rules.' | translate
                        "
                      >
                        <i class="far fa-exclamation-triangle text-danger"></i>
                      </custom-field-suffix>
                    }
                    <button
                      class="btn btn-link square"
                      customFieldSuffix
                      (click)="
                        form.controls.fallbacks.controls.customFields.controls[
                          field.name
                        ]!.setValue(null)
                      "
                      [disabled]="formValue.fallbacks.customFields[field.name] == null"
                      [ngbTooltip]="'Clear value' | translate"
                    >
                      <i class="far fa-times"></i>
                    </button>
                  </custom-field>
                }
              </ng-container>
              @if (
                uploader.permission()?.allowWriteDb &&
                uploader.currentConfig()?.mediaRoot?.showTags &&
                uploader.permission()?.showTags
              ) {
                <custom-field
                  [name]="
                    uploaderFlowFiles.length > 1
                      ? ('Shared tags' | translate)
                      : ('Tags' | translate)
                  "
                >
                  <media-tags-input
                    customFieldValue
                    [root]="uploader.currentConfig()!.mediaRoot"
                    formControlName="tags"
                    [allowNew]="true"
                  />
                </custom-field>
              }
              @if (uploaderFlowFiles.length > 1) {
                <message class="info" translate>
                  These custom fields and tags will be applied to all uploaded files. You can select
                  a specific file and use the “Asset overrides” tab to override custom fields'
                  values and/or list additional tags for each file individually.
                </message>
              }
            </ng-container>
          </li>
          @if (selectedFile && uploaderFlowFiles.length > 1) {
            <li ngbNavItem="overrides">
              <a ngbNavLink translate>Asset overrides</a>
              <ng-container *ngbNavContent formGroupName="overrides">
                <ng-container [formGroupName]="selectedFile.fullPath">
                  <ng-container formGroupName="customFields">
                    @for (field of fields; track field.name) {
                      <custom-field [name]="field.name">
                        <versatile-field
                          customFieldValue
                          [definition]="field.definition"
                          [formControl]="
                            form.controls.overrides.controls[selectedFile.fullPath]!.controls
                              .customFields.controls[field.name]!
                          "
                          [error]="
                            form.controls.overrides.controls[selectedFile.fullPath]!.controls
                              .customFields.controls[field.name]!.invalid
                          "
                          emphasizeAlmostNullValues
                          [getAutocompletions]="customFields.createAutocompletionGetter(field.name)"
                        />
                        @if (
                          analyzeRequirednessConstraint(field.name, selectedFile.fullPath) !==
                          "irrelevant"
                        ) {
                          <custom-field-suffix
                            class="square"
                            [ngbTooltip]="'This field is required.' | translate"
                          >
                            <i
                              class="far fa-asterisk"
                              [class.text-danger]="
                                analyzeRequirednessConstraint(field.name, selectedFile.fullPath) ===
                                'violated'
                              "
                              [class.text-success]="
                                analyzeRequirednessConstraint(field.name, selectedFile.fullPath) ===
                                'satisfied'
                              "
                            ></i>
                          </custom-field-suffix>
                        }
                        @if (getValueSource(field.name, selectedFile.fullPath) === "override") {
                          <custom-field-prefix
                            class="square"
                            [ngbTooltip]="
                              'This value overrides the provided shared value.' | translate
                            "
                          >
                            <i class="far fa-infinity text-muted"></i>
                          </custom-field-prefix>
                        }
                        @if (getValueSource(field.name, selectedFile.fullPath) === "fallback") {
                          <custom-field-prefix
                            class="square"
                            [ngbTooltip]="
                              'This field will take its value from the shared value.' | translate
                            "
                          >
                            <i class="far fa-infinity text-info"></i>
                          </custom-field-prefix>
                        }
                        @if (
                          form.controls.overrides.controls[selectedFile.fullPath]!.controls
                            .customFields.controls[field.name]!.invalid
                        ) {
                          <custom-field-suffix
                            class="square"
                            [ngbTooltip]="
                              'This value does not satisfy the validation rules.' | translate
                            "
                          >
                            <i class="far fa-exclamation-triangle text-danger"></i>
                          </custom-field-suffix>
                        }
                        <button
                          class="btn btn-link square"
                          customFieldSuffix
                          (click)="
                            form.controls.overrides.controls[
                              selectedFile.fullPath
                            ]!.controls.customFields.controls[field.name]!.setValue(null)
                          "
                          [disabled]="
                            formValue.overrides[selectedFile.fullPath]!.customFields![field.name] ==
                            null
                          "
                          [ngbTooltip]="'Clear value' | translate"
                        >
                          <i class="far fa-times"></i>
                        </button>
                      </custom-field>
                    }
                  </ng-container>
                  @if (
                    uploader.permission()?.allowWriteDb &&
                    uploader.currentConfig()?.mediaRoot?.showTags &&
                    uploader.permission()?.showTags
                  ) {
                    <custom-field [name]="'Additional tags' | translate">
                      <media-tags-input
                        customFieldValue
                        [root]="uploader.currentConfig()!.mediaRoot"
                        formControlName="tags"
                        [allowNew]="true"
                      />
                    </custom-field>
                  }
                </ng-container>
              </ng-container>
            </li>
          }
        </ul>
        <div [ngbNavOutlet]="nav"></div>
      </div>
    }
  </div>
  @if (uploaderFlowFiles.length > 0 && !allFilesDone()) {
    <button
      class="btn btn-primary d-block"
      (click)="startAll()"
      data-test-qa="startAllButton"
      [disabled]="
        form.invalid || !haveAllNecessaryMetadata() || (areAllUploadsInProgress() && !haveErrors())
      "
    >
      @if (areSomeUploadsInProgress()) {
        <i class="far fa-play"></i>
        <strong>
          {{ "START { count } REMAINING" | translate: { count: getPausedUploadsCount() } }}
        </strong>
      } @else if (areAllUploadsInProgress()) {
        <strong translate>UPLOAD IN PROGRESS</strong>
      } @else {
        <i class="far fa-play"></i>
        <strong translate>START ALL</strong>
      }
    </button>
  }
</div>
<div class="drag-hint" [class.visible]="uploader.showDragHint">
  <div class="cloud"><i class="fad fa-upload"></i></div>
</div>
